import { SidebarActionReveal } from '@affine/core/modules/app-sidebar/views';
import clsx from 'clsx';
import {
  type ButtonHTMLAttributes,
  forwardRef,
  type MouseEventHandler,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';

import { DisclosureChevron } from '../../../components/animation/disclosure-chevron';
import * as styles from './sidebar-account-item.css';

export const WorkspacePaneSidebarActionButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement>
>(function WorkspacePaneSidebarActionButton(
  { className, type = 'button', ...props },
  ref
) {
  return (
    <button
      {...props}
      ref={ref}
      className={clsx(styles.actionButton, className)}
      type={type}
    />
  );
});

export interface WorkspacePaneSidebarAccountItemProps {
  actions?: ReactNode;
  active?: boolean;
  avatar: ReactNode;
  avatarAction?: ReactNode;
  avatarClassName?: string;
  avatarVariant?: 'avatar' | 'providerLogo';
  className?: string;
  disabled?: boolean;
  dragHandleRef?: Ref<HTMLDivElement>;
  expanded?: boolean;
  animate?: boolean;
  editingTitle?: ReactNode;
  meta?: ReactNode;
  onClick: MouseEventHandler<HTMLButtonElement>;
  selected?: boolean;
  status?: ReactNode;
  testId?: string;
  title: ReactNode;
}

/** Shared top-level account/source row chrome for native workspace apps. */
export function WorkspacePaneSidebarAccountItem({
  actions,
  active = false,
  avatar,
  avatarAction,
  avatarClassName,
  avatarVariant = 'avatar',
  className,
  disabled = false,
  dragHandleRef,
  expanded,
  animate = true,
  editingTitle,
  meta,
  onClick,
  selected = active,
  status,
  testId,
  title,
}: WorkspacePaneSidebarAccountItemProps): ReactElement {
  return (
    <SidebarActionReveal
      actions={actions}
      actionsClassName={styles.actions}
      className={clsx(styles.root, className)}
      contentClassName={styles.content}
      data-active={active}
      data-selected={selected}
      data-has-meta={Boolean(meta)}
      data-sidebar-section-snap-target="true"
      ref={dragHandleRef}
    >
      {editingTitle ? (
        <div
          className={styles.button}
          data-testid={testId}
          data-disclosure={expanded !== undefined}
        >
          {expanded !== undefined ? (
            <DisclosureChevron expanded={expanded} animate={animate} />
          ) : null}
          <span
            className={clsx(
              styles.avatar,
              avatarVariant === 'providerLogo' && styles.providerLogoAvatar,
              avatarClassName
            )}
            aria-hidden="true"
          >
            {avatarAction ? null : avatar}
          </span>
          <span className={styles.text}>
            <span className={styles.title}>{editingTitle}</span>
            {meta ? (
              <span
                className={styles.meta}
                title={typeof meta === 'string' ? meta : undefined}
              >
                {meta}
              </span>
            ) : null}
          </span>
          {status ? (
            <span className={styles.status} data-sidebar-action-reveal-fade>
              {status}
            </span>
          ) : null}
        </div>
      ) : (
        <button
          aria-current={selected ? 'true' : undefined}
          aria-expanded={expanded}
          data-disclosure={expanded !== undefined}
          className={styles.button}
          data-testid={testId}
          disabled={disabled}
          type="button"
          onClick={onClick}
        >
          {expanded !== undefined ? (
            <DisclosureChevron expanded={expanded} animate={animate} />
          ) : null}
          <span
            className={clsx(
              styles.avatar,
              avatarVariant === 'providerLogo' && styles.providerLogoAvatar,
              avatarClassName
            )}
            aria-hidden="true"
          >
            {avatarAction ? null : avatar}
          </span>
          <span className={styles.text}>
            <span className={styles.title}>{title}</span>
            {meta ? (
              <span
                className={styles.meta}
                title={typeof meta === 'string' ? meta : undefined}
              >
                {meta}
              </span>
            ) : null}
          </span>
          {status ? (
            <span className={styles.status} data-sidebar-action-reveal-fade>
              {status}
            </span>
          ) : null}
        </button>
      )}
      {avatarAction ? (
        <span className={styles.avatarAction}>{avatarAction}</span>
      ) : null}
    </SidebarActionReveal>
  );
}
